import { Modal, Button, Alert } from "antd";
import api from "ApiClient";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useCallback, useState, useEffect } from "react";
import { ApiURL, AppURL } from "utils/Actions";
import { API_RESPONSE_STATUS } from "utils/Constants";
import { StaffUserType } from "utils/Types";
import AuthHelper from "components/auth/helpers";
import { useAppDispatch } from "redux/hooks";
import { setAfterTransferOwnership } from "redux/reducers/GlobalUiSlice";
import { isEmpty } from "lodash";
import { afterExpiry } from "redux/reducers/LoginTimeOutSlice";

interface MakeOwnerProps {
  open: boolean;
  onCancel: () => void;
  staff: StaffUserType;
}
export default function MakeOwner({ open, onCancel, staff }: MakeOwnerProps) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [errors, setErrors] = useState<string[] | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const logOut = useCallback(() => {
    dispatch(
      setAfterTransferOwnership({
        msg: "Admin role has been transferred.",
        afterTransferOwnership: true
      })
    );
    // stop timeout prompt
    dispatch(afterExpiry());
    AuthHelper.clearAuth();
  }, []);
  const handleTransfer = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const result = await api.post(ApiURL.transferRole, {
        email: staff.email,
        seqNum: staff.seqNum
      });
      if (result.data.status === API_RESPONSE_STATUS.SUCCESS) {
        logOut();
      } else {
        if (result.data.errors) {
          setErrors(result.data.errors);
        } else {
          setErrors([t("makeOwner.errorMessage")]);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const onClearAuth = () => {
    const auth = AuthHelper.getAuth();
    if (isEmpty(auth)) {
      history.push(AppURL.login);
    }
  };

  useEffect(() => {
    window.addEventListener("storage", onClearAuth);
    return () => {
      window.removeEventListener("storage", onClearAuth);
    };
  }, [onClearAuth]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={t("makeOwner.heading")}
      closable={!isSubmitting}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={isSubmitting}>
          {t("makeOwner.cancelCTA")}
        </Button>,
        <Button
          key="transfer"
          type="primary"
          onClick={handleTransfer}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {t("makeOwner.confirmCTA")}
        </Button>
      ]}
    >
      {errors && (
        <Alert
          message={errors.join(" ")}
          type="error"
          closable
          showIcon
          style={{ width: "100%", marginBottom: 24 }}
        />
      )}
      <Trans>{t("makeOwner.description", { email: staff?.email })}</Trans>
    </Modal>
  );
}
